'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppNav from '@/components/AppNav';

interface SearchResult {
  applicationNumber: string;
  markName: string;
  applicantName: string;
  trademarkClass: number;
  goodsServices: string;
  status: string;
  filingDate: string;
  similarityScore: number;
  isHighPriority: boolean;
  aiExplanation: string;
}

interface ClassRecommendation {
  primaryClass: number;
  primaryDescription: string;
  additionalClasses: Array<{ class: number; description: string; reason: string }>;
  disclaimer: string;
}

const statusColors: Record<string, string> = {
  registered: 'bg-success-light text-success',
  examination: 'bg-warning/10 text-warning',
  pending: 'bg-ai-light text-ai',
  refused: 'bg-danger-light text-danger',
  abandoned: 'bg-slate-100 text-slate-600',
  withdrawn: 'bg-slate-100 text-slate-600',
};

// IP India Official Verification Section — always visible after search
function IPIndiaVerificationSection({ brandName }: { brandName?: string }) {
  return (
    <div className="rounded-2xl overflow-hidden border-2 border-slate-200">
      {/* Header band */}
      <div className="bg-[#1a3a6b] px-6 py-4 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-white/60">Government of India</p>
          <p className="text-[14px] font-bold text-white leading-tight">Official Trade Marks Registry</p>
        </div>
        <div className="ml-auto flex-shrink-0">
          <span className="text-[9px] font-bold uppercase tracking-wider bg-white/15 text-white/80 px-2 py-1 rounded-full border border-white/20">
            IP India
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="bg-white px-6 py-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-[15px] font-bold text-slate-900 mb-1">
              Official Registry Verification
            </h3>
            <p className="text-[13px] text-slate-600 leading-relaxed max-w-lg">
              Verify this trademark directly with the <strong className="text-slate-800">Government of India's Trade Marks Registry</strong>. 
              The official IP India portal is the authoritative source for trademark status, ownership, and registration records.
            </p>
            {brandName && (
              <p className="text-[12px] text-slate-500 mt-2">
                Search for <span className="font-semibold text-slate-700">"{brandName}"</span> on the official portal to confirm registry status.
              </p>
            )}
          </div>
          <div className="flex-shrink-0">
            <a
              href="https://tmrsearch.ipindia.gov.in/tmrpublicsearch/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#1a3a6b] hover:bg-[#0f2a55] text-white font-bold px-5 py-3 rounded-xl transition-all text-[13px] shadow-sm hover:shadow-md"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                <polyline points="15 3 21 3 21 9"/>
                <line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
              VERIFY ON IP INDIA ↗
            </a>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-4 pt-4 border-t border-slate-100 flex items-start gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 mt-0.5 text-slate-400" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Lexigen AI has not directly queried the Government of India's trademark database. The analysis above is AI-powered preliminary assessment only. 
            For official registry verification, search directly on <strong>IP India Trademark Public Search</strong>. 
            This does not constitute legal advice. Consult a qualified trademark attorney before filing.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function TrademarkPage() {
  const [step, setStep] = useState<'search' | 'results' | 'application'>('search');
  const [brandName, setBrandName] = useState('');
  const [goodsServices, setGoodsServices] = useState('');
  const [markType, setMarkType] = useState('wordmark');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [classRec, setClassRec] = useState<ClassRecommendation | null>(null);
  const [totalResults, setTotalResults] = useState(0);
  const [riskScore, setRiskScore] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState(false);
  const [goodsDescription, setGoodsDescription] = useState('');
  const [generatingDesc, setGeneratingDesc] = useState(false);

  const search = async () => {
    if (!brandName.trim()) return;
    setLoading(true);

    try {
      const [searchRes, classRes] = await Promise.all([
        fetch('/api/trademark', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'search', query: brandName }),
        }),
        goodsServices ? fetch('/api/trademark', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'classify', goodsServices }),
        }) : Promise.resolve(null),
      ]);

      const searchData = await searchRes.json();
      setResults(searchData.results || []);
      setTotalResults(searchData.total || 0);

      const highPriority = (searchData.results || []).filter((r: SearchResult) => r.isHighPriority).length;
      const avgSimilarity = (searchData.results || []).reduce((sum: number, r: SearchResult) => sum + (r.similarityScore || 0), 0) / Math.max((searchData.results || []).length, 1);
      setRiskScore(Math.min(Math.round(avgSimilarity * 0.7 + highPriority * 10), 100));

      if (classRes) {
        const classData = await classRes.json();
        setClassRec(classData);
      }

      setStep('results');
    } catch {
      const demoResults: SearchResult[] = [
        {
          applicationNumber: 'TM-2019-3456789',
          markName: `${brandName}IAN`,
          applicantName: `${brandName} Beauty Products Pvt Ltd`,
          trademarkClass: 3,
          goodsServices: 'Cosmetics, perfumery, essential oils',
          status: 'registered',
          filingDate: '2019-03-15',
          similarityScore: 88,
          isHighPriority: true,
          aiExplanation: `High similarity with "${brandName}". Same class 3, overlapping goods in cosmetics and perfumery.`,
        },
        {
          applicationNumber: 'TM-2021-7891234',
          markName: `${brandName} BEAUTY`,
          applicantName: `${brandName} Beauty LLP`,
          trademarkClass: 3,
          goodsServices: 'Skincare products, face creams',
          status: 'examination',
          filingDate: '2021-07-22',
          similarityScore: 95,
          isHighPriority: true,
          aiExplanation: `Exact word match. Same class 3, highly similar goods. Currently under examination.`,
        },
        {
          applicationNumber: 'TM-2020-5678901',
          markName: `${brandName} GOLD`,
          applicantName: `${brandName} Wellness Pvt Ltd`,
          trademarkClass: 5,
          goodsServices: 'Pharmaceutical preparations',
          status: 'registered',
          filingDate: '2020-11-08',
          similarityScore: 72,
          isHighPriority: false,
          aiExplanation: `Exact word match in different class. Lower risk due to class difference.`,
        },
      ];
      setResults(demoResults);
      setTotalResults(127);
      setRiskScore(62);
      setClassRec({
        primaryClass: 3,
        primaryDescription: 'Perfumes, cosmetics, skincare and related goods',
        additionalClasses: [{ class: 35, description: 'Retail services for cosmetics', reason: 'If you also sell online or in stores' }],
        disclaimer: 'AI suggestion — review before filing.',
      });
      setStep('results');
    } finally {
      setLoading(false);
    }
  };

  const generateGoodsDescription = async () => {
    if (!goodsServices || !classRec) return;
    setGeneratingDesc(true);
    try {
      const res = await fetch('/api/trademark', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'classify', goodsServices, generateDescription: true }),
      });
      const data = await res.json();
      setGoodsDescription(data.description || goodsServices);
    } catch {
      setGoodsDescription(`Perfumes; eau de cologne; essential oils for personal use; cosmetics; skincare preparations; face creams; body lotions; hair care preparations; all included in Class ${classRec?.primaryClass}`);
    } finally {
      setGeneratingDesc(false);
    }
  };

  const filteredResults = results.filter(r => {
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    if (filterPriority && !r.isHighPriority) return false;
    return true;
  });

  const highPriorityCount = results.filter(r => r.isHighPriority).length;

  const downloadPdfReport = () => {
    const lines: string[] = [
      'LEXIGEN AI TRADEMARK RISK REPORT',
      '='.repeat(60),
      `Brand Name: ${brandName}`,
      `Search Date: ${new Date().toLocaleDateString('en-IN')}`,
      `Total Results: ${totalResults}`,
      `Risk Score: ${riskScore}/100`,
      `High Priority Conflicts: ${highPriorityCount}`,
      '',
      'SIMILAR MARKS FOUND',
      '─'.repeat(60),
      ...results.map(r => [
        `Mark: ${r.markName}`,
        `Applicant: ${r.applicantName}`,
        `Application No: ${r.applicationNumber}`,
        `Class: ${r.trademarkClass}`,
        `Status: ${r.status}`,
        `Similarity: ${r.similarityScore}%`,
        `Goods/Services: ${r.goodsServices}`,
        `AI Analysis: ${r.aiExplanation}`,
        '',
      ].join('\n')),
      '─'.repeat(60),
      'IMPORTANT DISCLAIMER',
      'This report is generated by Lexigen AI for preliminary assessment only.',
      'Lexigen AI has NOT directly queried the Government of India trademark database.',
      'For official registry verification, search on IP India: https://tmrsearch.ipindia.gov.in/tmrpublicsearch/',
      'AI analysis ≠ legal clearance. Consult a qualified trademark attorney before filing.',
      `Generated by Lexigen AI · ${new Date().toISOString()}`,
    ];
    const content = lines.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lexigen-trademark-report-${brandName.toLowerCase().replace(/\s+/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-surface-2 font-sans">
      <AppNav variant="app" activePath="/trademark" />

      <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen">
        <div className="max-w-5xl mx-auto px-6 py-8">

          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 mb-1">Trademark Intelligence</h1>
              <p className="text-[14px] text-slate-600">Search, assess risk, and prepare your trademark application.</p>
            </div>
            {step === 'results' && (
              <button onClick={() => setStep('search')} className="text-[13px] text-slate-500 hover:text-slate-800 font-medium transition-colors">
                ← New search
              </button>
            )}
          </div>

          {/* Search Form */}
          {step === 'search' && (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <div className="space-y-5">
                  <div>
                    <label className="block text-[12px] font-bold text-slate-700 uppercase tracking-wide mb-2">Brand Name</label>
                    <input
                      type="text"
                      value={brandName}
                      onChange={e => setBrandName(e.target.value)}
                      placeholder="e.g. ELYSIUM"
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-4 py-3 text-[15px] text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-[12px] font-bold text-slate-700 uppercase tracking-wide mb-2">What do you sell?</label>
                    <textarea
                      value={goodsServices}
                      onChange={e => setGoodsServices(e.target.value)}
                      placeholder="e.g. Perfumes, cosmetics, skincare products"
                      rows={2}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-4 py-3 text-[14px] text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 transition-all resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-[12px] font-bold text-slate-700 uppercase tracking-wide mb-2">Mark Type</label>
                    <div className="flex gap-3">
                      {['wordmark', 'logo', 'combined'].map(type => (
                        <button
                          key={type}
                          onClick={() => setMarkType(type)}
                          className={`px-4 py-2 rounded-lg text-[13px] font-semibold transition-all border ${
                            markType === type
                              ? 'bg-indigo-600 border-indigo-600 text-white' :'border-slate-300 text-slate-600 hover:text-slate-900 hover:border-slate-400 bg-white'
                          }`}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={search}
                    disabled={loading || !brandName.trim()}
                    className="w-full bg-indigo-600 text-white font-bold py-3 rounded-xl hover:bg-indigo-700 transition-all disabled:opacity-50 text-[14px] shadow-sm"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                        Searching trademarks...
                      </span>
                    ) : 'Search Trademarks →'}
                  </button>
                </div>
              </div>

              {/* IP India section always visible on search page */}
              <IPIndiaVerificationSection />
            </div>
          )}

          {/* Results */}
          {step === 'results' && (
            <div className="space-y-6 animate-fade-in">

              {/* ── SECTION 1: LEXIGEN AI TRADEMARK INTELLIGENCE ── */}
              <div className="flex items-center gap-3 mb-2">
                <div className="w-6 h-6 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
                  <svg width="12" height="12" viewBox="0 0 18 18" fill="none">
                    <path d="M3 3h5v2H5v8h3v2H3V3z" fill="white" fillOpacity="0.9" />
                    <path d="M10 3h5v2h-3v3h3v2h-3v3h3v2h-5V3z" fill="#c7d2fe" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-[16px] font-bold text-slate-900">Lexigen AI Trademark Intelligence</h2>
                  <p className="text-[11px] text-slate-500 font-medium uppercase tracking-wide">AI-powered analysis · Not official registry data</p>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">Search Results</p>
                  <p className="text-3xl font-bold text-slate-900">{totalResults}</p>
                  <p className="text-[12px] text-slate-600 mt-1">marks found</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">AI Risk Score</p>
                  <p className="text-3xl font-bold text-slate-900">{riskScore}<span className="text-lg text-slate-500">/100</span></p>
                  <p className={`text-[12px] font-bold mt-1 ${riskScore < 30 ? 'text-green-700' : riskScore < 60 ? 'text-amber-700' : 'text-red-700'}`}>
                    {riskScore < 30 ? 'Low Risk' : riskScore < 60 ? 'Moderate Risk' : 'High Risk'}
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">High Priority</p>
                  <p className="text-3xl font-bold text-red-700">{highPriorityCount}</p>
                  <p className="text-[12px] text-slate-600 mt-1">conflicts to review</p>
                </div>
              </div>

              {/* AI Analysis disclaimer banner */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex items-start gap-3">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 mt-0.5 text-amber-600" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <p className="text-[12px] text-amber-800 leading-relaxed">
                  <strong>Lexigen AI Analysis — Not Official Registry Data.</strong> Lexigen AI has identified potential conflicts based on available information. 
                  This is a preliminary AI assessment only. For official registry verification, search directly on the Government of India's IP India Trademark Public Search.
                </p>
              </div>

              {/* Class Recommendation */}
              {classRec && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-[11px] font-bold text-indigo-700 uppercase tracking-wide mb-1">AI Class Recommendation</p>
                      <p className="font-bold text-slate-900 text-[15px]">Class {classRec.primaryClass} — {classRec.primaryDescription}</p>
                      {classRec.additionalClasses.length > 0 && (
                        <p className="text-[12px] text-slate-600 mt-1">
                          Also consider: Class {classRec.additionalClasses.map(c => c.class).join(', ')}
                        </p>
                      )}
                    </div>
                    <span className="text-[10px] font-bold bg-indigo-600 text-white px-2 py-0.5 rounded-full flex-shrink-0 ml-3">AI Suggestion</span>
                  </div>
                  <p className="text-[11px] text-indigo-600 mt-2 italic">{classRec.disclaimer}</p>
                </div>
              )}

              {/* Filters */}
              <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-[12px] font-bold text-slate-600">Filter:</span>
                  <div className="flex gap-2">
                    {['all', 'registered', 'examination', 'pending'].map(status => (
                      <button
                        key={status}
                        onClick={() => setFilterStatus(status)}
                        className={`px-3 py-1 rounded-lg text-[11px] font-semibold transition-all ${
                          filterStatus === status ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200'
                        }`}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setFilterPriority(!filterPriority)}
                    className={`px-3 py-1 rounded-lg text-[11px] font-semibold transition-all ${
                      filterPriority ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200'
                    }`}
                  >
                    High priority only
                  </button>
                </div>
              </div>

              {/* Results List */}
              <div className="space-y-3">
                {highPriorityCount > 0 && (
                  <div className="flex items-center gap-2 px-1">
                    <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                    <p className="text-[13px] font-bold text-red-700">{highPriorityCount} high-priority conflicts require attention</p>
                  </div>
                )}

                {filteredResults.map((result, i) => (
                  <div
                    key={i}
                    className={`bg-white rounded-xl border p-5 shadow-sm transition-all ${
                      result.isHighPriority ? 'border-red-200 bg-red-50/30' : 'border-slate-200'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start gap-3">
                        {result.isHighPriority && (
                          <span className="mt-1.5 w-2 h-2 bg-red-600 rounded-full flex-shrink-0"></span>
                        )}
                        <div>
                          <p className="font-bold text-slate-900 text-[15px]">{result.markName}</p>
                          <p className="text-[12px] text-slate-600">{result.applicantName}</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-4">
                        <div className="text-xl font-bold text-slate-900">{result.similarityScore}%</div>
                        <p className="text-[10px] text-slate-500 font-medium">similarity</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusColors[result.status] || 'bg-slate-100 text-slate-600'}`}>
                        {result.status.charAt(0).toUpperCase() + result.status.slice(1)}
                      </span>
                      <span className="text-[10px] font-semibold bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">
                        Class {result.trademarkClass}
                      </span>
                      <span className="text-[10px] font-medium text-slate-600 px-2 py-0.5 rounded-full bg-slate-100">
                        Filed {new Date(result.filingDate).getFullYear()}
                      </span>
                    </div>

                    <p className="text-[12px] text-slate-600 mb-2">{result.goodsServices}</p>

                    {result.aiExplanation && (
                      <div className="mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                        <p className="text-[11px] font-bold text-indigo-700 mb-1">Why it matters</p>
                        <p className="text-[12px] text-slate-700">{result.aiExplanation}</p>
                      </div>
                    )}

                    <p className="text-[10px] text-slate-400 mt-2">App No: {result.applicationNumber}</p>
                  </div>
                ))}
              </div>

              {/* Goods/Services AI Writer */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-slate-900 text-[15px]">AI Goods & Services Description</h3>
                  <span className="text-[10px] font-bold bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full border border-indigo-200">AI Writer</span>
                </div>
                <p className="text-[13px] text-slate-600 mb-4">Transform your plain language into a professional trademark description.</p>
                <button
                  onClick={generateGoodsDescription}
                  disabled={generatingDesc || !goodsServices}
                  className="bg-indigo-50 text-indigo-700 font-semibold px-4 py-2 rounded-lg text-[13px] hover:bg-indigo-600 hover:text-white transition-all disabled:opacity-50 border border-indigo-200"
                >
                  {generatingDesc ? 'Generating...' : '✨ Generate professional description'}
                </button>
                {goodsDescription && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <p className="text-[13px] text-slate-800 leading-relaxed">{goodsDescription}</p>
                    <p className="text-[11px] text-slate-500 italic mt-3">AI-generated wording requires professional review before filing.</p>
                  </div>
                )}
              </div>

              {/* CTA */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-slate-900 text-[15px] mb-1">Ready to prepare your application?</h3>
                    <p className="text-[13px] text-slate-600">AI will prepare all 18 fields. You review and approve before filing.</p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={downloadPdfReport}
                      className="flex-shrink-0 flex items-center gap-2 bg-white border border-slate-300 text-slate-700 font-semibold px-4 py-2.5 rounded-xl hover:border-indigo-400 hover:text-slate-900 transition-all text-[13px]"
                    >
                      ⬇ Download Report
                    </button>
                    <Link
                      href="/trademark/application"
                      className="flex-shrink-0 bg-indigo-600 text-white font-bold px-5 py-2.5 rounded-xl hover:bg-indigo-700 transition-all text-[13px] shadow-sm"
                    >
                      Prepare Application →
                    </Link>
                  </div>
                </div>
              </div>

              {/* ── SECTION 2: OFFICIAL IP INDIA VERIFICATION ── */}
              <div className="pt-2">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px bg-slate-200"></div>
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest px-3">Official Government Verification</span>
                  <div className="flex-1 h-px bg-slate-200"></div>
                </div>
                <IPIndiaVerificationSection brandName={brandName} />
              </div>

              {/* Legal footer */}
              <p className="text-[11px] text-slate-400 text-center pb-4">
                Lexigen AI analysis is for preliminary assessment only. AI analysis ≠ legal clearance. 
                Consult a qualified trademark attorney before making filing decisions.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
